import {
  OrbitControls,
  OrbitControlsChangeEvent,
  OrbitControlsProps,
} from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react'
import { Vector3 } from 'three'

import { DEFAULT_CAMERA_CONTROLS, DEFAULT_CANVAS_CONTROLS } from './constants'
import SpringAnimate from './spring-animate'
import Throttle from './throttle'

export interface CameraControlsRefProps {
  moveCamera: () => void
  stopCamera: () => void
  spawnCamera: (position: Vector3, target: Vector3) => void
  setPosition: (position: Vector3) => void
  setTarget: (target: Vector3) => void
}

export interface CameraControlsProps {
  getPosition?: (position: Vector3) => void
  getTarget?: (target: Vector3) => void
  getCanAnimate?: (status: boolean) => void
  detectChange?: (arg: boolean) => void
  initPosition: Vector3
  initTarget: Vector3
  isMoving?: (arg: boolean) => void
  isCameraLoaded?: (arg: boolean) => void
  maxDistance?: number
  minDistance?: number
  minPolarAngle?: number
  maxPolarAngle?: number
  enablePan?: boolean
  disableCameraLoadingCntrl?: boolean
  near?: number
  far?: number
}

const CameraControls = forwardRef<
  CameraControlsRefProps | undefined,
  CameraControlsProps
>(
  (
    {
      getTarget,
      getPosition,
      getCanAnimate,
      detectChange,
      initPosition,
      initTarget,
      isMoving,
      maxDistance = DEFAULT_CAMERA_CONTROLS.maxDistance,
      minDistance = DEFAULT_CAMERA_CONTROLS.minDistance,
      minPolarAngle = DEFAULT_CAMERA_CONTROLS.minPolarAngle,
      maxPolarAngle = DEFAULT_CAMERA_CONTROLS.maxPolarAngle,
      isCameraLoaded,
      enablePan = false,
      disableCameraLoadingCntrl = false,
      near,
      far,
    },
    ref
  ) => {
    const orbitRef = useRef(null)
    const isUserInteracting = useRef(false)

    const { camera } = useThree()

    const springAnimate = SpringAnimate({
      initTarget: [initTarget.x, initTarget.y, initTarget.z],
      initPosition: [initPosition.x, initPosition.y, initPosition.z],
    })

    const distanceLimits = React.useMemo(() => {
      const isValidDistance = maxDistance > minDistance

      return {
        maxDistance: isValidDistance
          ? maxDistance
          : DEFAULT_CAMERA_CONTROLS.maxDistance,
        minDistance: isValidDistance
          ? minDistance
          : DEFAULT_CAMERA_CONTROLS.minDistance,
      }
    }, [maxDistance, minDistance])

    const moveCamera = () => {
      springAnimate.setCanAnimate(true)
    }

    const stopCamera = () => {
      springAnimate.setCanAnimate(false)
    }

    const setPosition = (pos: Vector3) => {
      springAnimate.setPosition([pos?.x || 0, pos?.y || 0, pos?.z || 0])
    }

    const setTarget = (pos: Vector3) => {
      springAnimate.setTarget([pos?.x || 0, pos?.y || 0, pos?.z || 0])
    }

    const setSpringAnimationTarget = (currentTarget: Vector3) => {
      if (currentTarget) {
        springAnimate.springs.target.set([
          currentTarget?.x || 0,
          currentTarget?.y || 0,
          currentTarget?.z || 0,
        ])
      }
    }

    const setSpringAnimationPosition = (currentPosition: Vector3) => {
      if (currentPosition) {
        springAnimate.springs.position.set([
          currentPosition?.x || 0,
          currentPosition?.y || 0,
          currentPosition?.z || 0,
        ])
      }
    }

    const spawnCamera = (pos: Vector3, tar: Vector3) => {
      if (camera) {
        setTarget(tar)
        setSpringAnimationTarget(tar)
        setPosition(pos)
        setSpringAnimationPosition(pos)
        camera.position.set(pos.x, pos.y, pos.z)
      }
    }

    useImperativeHandle(ref, () => ({
      stopCamera,
      spawnCamera,
      moveCamera,
      setPosition,
      setTarget,
    }))

    useFrame(() => {
      if (springAnimate.canAnimate) {
        springAnimate.springs.target.to((x: number, y: number, z: number) => {
          if (orbitRef.current) {
            const orbitControl = orbitRef?.current as OrbitControlsProps
            orbitControl.target = new Vector3(x, y, z)
          }
        })
      }
    })

    useFrame(() => {
      if (springAnimate.canAnimate) {
        springAnimate.springs.position.to((x: number, y: number, z: number) => {
          if (camera) {
            camera.position.set(x, y, z)
          }
          if (
            JSON.stringify([x, y, z]) === JSON.stringify(springAnimate.position)
          ) {
            springAnimate.setCanAnimate(false)
          }
        })
      }
    })

    React.useEffect(() => {
      getCanAnimate?.(springAnimate.canAnimate)
    }, [getCanAnimate, springAnimate.canAnimate])

    React.useEffect(() => {
      if (orbitRef.current) {
        const orbitControl = orbitRef?.current as OrbitControlsProps
        if (orbitControl) {
          orbitControl.target = initTarget
        }
      }
    }, [initTarget, orbitRef])

    React.useEffect(() => {
      if (camera) {
        camera.position.set(initPosition.x, initPosition.y, initPosition.z)
      }
    }, [initPosition])

    React.useEffect(() => {
      if (!camera) {
        return
      }

      camera.near = near ?? DEFAULT_CANVAS_CONTROLS.near
      camera.far = far ?? DEFAULT_CANVAS_CONTROLS.far
      camera.updateProjectionMatrix()
    }, [camera, near, far])

    const handleOnStart = useCallback(() => {
      isUserInteracting.current = true
      if (springAnimate) {
        springAnimate.setCanAnimate(false)
      }
    }, [])

    const handleGetTarget = (target: Vector3) => {
      if (getTarget) {
        getTarget(target)
      }
    }

    const handleGetPosition = (position: Vector3) => {
      if (getPosition) {
        getPosition(position)
      }
    }

    const handleDetectPosition = () => {
      if (detectChange) {
        detectChange(true)
      }
    }

    const handleOnEnd = useCallback(() => {
      isUserInteracting.current = false
      if (isMoving) {
        isMoving(false)
      }
      if (orbitRef?.current) {
        const position = (
          orbitRef?.current as OrbitControlsProps
        ).object?.getWorldPosition(new Vector3())
        const target = (orbitRef?.current as OrbitControlsProps)
          .target as Vector3
        if (target) {
          handleGetTarget(target)
          setSpringAnimationTarget(target)
        }
        if (position) {
          handleGetPosition(position)
          handleDetectPosition()
          setSpringAnimationPosition(position)
        }
      }
    }, [handleGetPosition, handleGetTarget])

    const throttledPositionUpdate = useCallback(
      Throttle((e: OrbitControlsChangeEvent | undefined) => {
        if (e) {
          const position = e.target?.object?.getWorldPosition(new Vector3())
          if (position) {
            handleGetPosition(position)
          }
        }
      }, 150),
      [handleGetPosition, Throttle]
    )

    const handleOnChange = useCallback(
      (e: OrbitControlsChangeEvent | undefined) => {
        if (isMoving && isUserInteracting.current) {
          isMoving(true)
        }

        throttledPositionUpdate(e)
      },
      [throttledPositionUpdate, isMoving]
    )

    React.useEffect(() => {
      if (camera) {
        springAnimate.setCanAnimate(true)
        setSpringAnimationPosition(initPosition)
        camera.position.set(initPosition.x, initPosition.y, initPosition.z)
        if (isCameraLoaded && !disableCameraLoadingCntrl) {
          isCameraLoaded(true)
        }
      }
    }, [isCameraLoaded, disableCameraLoadingCntrl, camera])

    return (
      <OrbitControls
        camera={camera}
        target={initTarget}
        enablePan={enablePan}
        enableDamping={false}
        ref={orbitRef}
        onStart={() => handleOnStart()}
        onEnd={() => handleOnEnd()}
        onChange={handleOnChange}
        minDistance={distanceLimits.minDistance}
        maxDistance={distanceLimits.maxDistance}
        minPolarAngle={minPolarAngle}
        maxPolarAngle={maxPolarAngle}
        makeDefault
      />
    )
  }
)

export default CameraControls
